import React, { useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { LINK } from '../../appearance/Colors';
import LayoutDefault from '../Landing/layouts/LayoutDefault';
import Markdown from 'markdown-to-jsx';
import { BLOG_POSTS, BLOG_META } from '../../data/blogPosts';
import { getBlogCategoryEntries, slugifyBlogCategory } from '../../data/blogCategories';
import { FaFacebook, FaEnvelope, FaRssSquare } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';

// Scroll restoration component
const ScrollToTop = () => {
    const { pathname } = useLocation();
    
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);
    
    return null;
};

const BlogPost = () => {
    const { slug } = useParams();
    const { pathname } = useLocation();
    const post = BLOG_POSTS[slug];

    // Scroll to top when pathname (URL) changes
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    // Ensure absolute URL for social sharing
    const postUrl = `https://luckynote.io/blog/${slug}`;
    const formattedDate = post
        ? new Date(post.createdAt).toLocaleDateString('en-US', {
            month: 'numeric',
            day: 'numeric',
            year: 'numeric'
        })
        : '';
    const categoryPath = post?.category ? `/blog/category/${slugifyBlogCategory(post.category)}` : null;
    const currentCategorySlug = post?.category ? slugifyBlogCategory(post.category) : null;
    const discoveryCategories = getBlogCategoryEntries();
    
    const handleShare = (platform) => {
        console.log(`Sharing to ${platform}:`, {
            url: postUrl,
            title: post.title,
            platform
        });
    };

    if (!post) {
        return (
            <LayoutDefault>
                <BlogSurface>
                    <BlogContainer>
                        <NotFound>
                            <h1>Post Not Found</h1>
                            <p>The blog post you're looking for doesn't exist.</p>
                            <BackToBlogs to="/blog">← Back to Blog</BackToBlogs>
                        </NotFound>
                    </BlogContainer>
                </BlogSurface>
            </LayoutDefault>
        );
    }

    return (
        <LayoutDefault>
            <BlogSurface>
                <BlogContainer>
                    <Breadcrumbs aria-label="Blog breadcrumbs">
                        <BackToBlogs to="/blog">Blog</BackToBlogs>
                        <BreadcrumbSeparator aria-hidden="true">›</BreadcrumbSeparator>
                        {post.category && (
                            <BreadcrumbCategory
                                to={{
                                    pathname: categoryPath,
                                    state: { preserveScroll: true }
                                }}
                            >
                                {post.category}
                            </BreadcrumbCategory>
                        )}
                    </Breadcrumbs>

                    <Article>
                        <BlogHeader>
                            <h1>{post.title}</h1>
                            <Dek>{post.excerpt || post.metaDescription}</Dek>
                            <BlogMeta>
                                <MetaRow>
                                    <span>Written by</span>
                                    <AuthorPill>
                                        <AuthorAvatar src={post.author.avatar} alt={post.author.name} />
                                        {post.author.name}
                                    </AuthorPill>
                                </MetaRow>
                                <MetaRow>
                                    <span>Published on</span>
                                    <MetaPill>{formattedDate}</MetaPill>
                                    {post.category && (
                                        <>
                                            <span>in</span>
                                            <CategoryTag
                                                to={{
                                                    pathname: categoryPath,
                                                    state: { preserveScroll: true }
                                                }}
                                            >
                                                {post.category}
                                            </CategoryTag>
                                        </>
                                    )}
                                </MetaRow>
                            </BlogMeta>
                        </BlogHeader>

                        {post.featuredImage && (
                            <FeaturedImage src={post.featuredImage} alt={post.title} />
                        )}

                        <BlogContent>
                            <Markdown
                                options={{
                                    overrides: {
                                        h1: {
                                            component: ({ children, ...props }) => (
                                                <h2 {...props}>{children}</h2>
                                            ),
                                            props: {
                                                className: 'blog-heading-h2'
                                            }
                                        },
                                        h2: {
                                            component: ({ children, ...props }) => (
                                                <h2 {...props}>{children}</h2>
                                            ),
                                            props: {
                                                className: 'blog-heading-h2'
                                            }
                                        },
                                        h3: {
                                            component: ({ children, ...props }) => (
                                                <h3 {...props}>{children}</h3>
                                            ),
                                            props: {
                                                className: 'blog-heading-h3'
                                            }
                                        },
                                        p: {
                                            component: ({ children, ...props }) => (
                                                <p {...props} className="blog-paragraph">{children}</p>
                                            )
                                        },
                                        ul: {
                                            component: ({ children, ...props }) => (
                                                <ul {...props}>{children}</ul>
                                            ),
                                            props: {
                                                className: 'blog-list'
                                            }
                                        },
                                        ol: {
                                            component: ({ children, ...props }) => (
                                                <ol {...props}>{children}</ol>
                                            ),
                                            props: {
                                                className: 'blog-list'
                                            }
                                        },
                                        li: {
                                            component: ({ children, ...props }) => (
                                                <li {...props}>{children}</li>
                                            ),
                                            props: {
                                                className: 'blog-list-item'
                                            }
                                        },
                                        input: {
                                            component: props => <input {...props} disabled />
                                        },
                                        hr: {
                                            component: props => <hr {...props} className="blog-rule" />
                                        },
                                        table: {
                                            component: ({ children, ...props }) => (
                                                <div className="table-container">
                                                    <table {...props}>{children}</table>
                                                </div>
                                            ),
                                            props: {
                                                className: 'blog-table'
                                            }
                                        },
                                        tr: {
                                            component: props => <tr {...props} />,
                                            props: {
                                                className: 'blog-table-row'
                                            }
                                        },
                                        th: {
                                            component: props => <th {...props} />,
                                            props: {
                                                className: 'blog-table-header'
                                            }
                                        },
                                        td: {
                                            component: props => <td {...props} />,
                                            props: {
                                                className: 'blog-table-cell'
                                            }
                                        },
                                        img: {
                                            component: ({ src, alt, ...props }) => {
                                                if (src && /\.(mp4|webm|mov)$/i.test(src)) {
                                                    return (
                                                        <MarkdownVideo
                                                            src={src}
                                                            autoPlay
                                                            loop
                                                            muted
                                                            playsInline
                                                        />
                                                    );
                                                }
                                                return <MarkdownImage src={src} alt={alt} {...props} />;
                                            }
                                        },
                                        a: {
                                            component: ({ children, ...props }) => (
                                                <a {...props} target="_blank" rel="noopener noreferrer">{children}</a>
                                            ),
                                            props: {
                                                className: 'blog-link'
                                            }
                                        },
                                        blockquote: {
                                            component: ({ children, ...props }) => (
                                                <blockquote {...props}>{children}</blockquote>
                                            ),
                                            props: {
                                                className: 'blog-blockquote'
                                            }
                                        }
                                    }
                                }}
                            >
                                {post.content}
                            </Markdown>
                        </BlogContent>

                    {discoveryCategories.length > 0 && (
                        <BlogDiscovery aria-labelledby="blog-discovery-title">
                            <DiscoveryTitle id="blog-discovery-title">
                                Discover More from the Blog
                                <FaRssSquare aria-hidden="true" />
                            </DiscoveryTitle>
                            <DiscoveryLinks>
                                {discoveryCategories.map(category => (
                                    <DiscoveryLink
                                        key={category.slug}
                                        to={{
                                            pathname: `/blog/category/${category.slug}`,
                                            state: { preserveScroll: true }
                                        }}
                                        $active={category.slug === currentCategorySlug}
                                    >
                                        {category.name}
                                    </DiscoveryLink>
                                ))}
                            </DiscoveryLinks>
                        </BlogDiscovery>
                    )}

                    <SocialShare>
                        <ShareText>Share on:</ShareText>
                        <ShareButtons>
                            <ShareButton
                                href={`https://x.com/intent/tweet?text=${encodeURIComponent(`${post.title}\n\n${postUrl}`)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Share on X"
                                onClick={() => handleShare('X')}
                            >
                                <FaXTwitter size={22} />
                            </ShareButton>
                            <ShareButton
                                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(postUrl)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Share on Facebook"
                                onClick={() => handleShare('Facebook')}
                            >
                                <FaFacebook size={22} />
                            </ShareButton>
                            <ShareButton
                                href={`mailto:?subject=${encodeURIComponent(`${post.title} - ${BLOG_META.siteName}`)}&body=${encodeURIComponent(`Check out this article: ${postUrl}`)}`}
                                aria-label="Share via Email"
                                onClick={() => handleShare('Email')}
                            >
                                <FaEnvelope size={22} />
                            </ShareButton>
                        </ShareButtons>
                    </SocialShare>

                    <AuthorCard>
                        <AuthorImage src={post.author.avatar} alt={post.author.name} />
                        <AuthorInfoContainer>
                            <AuthorLabel>Article by</AuthorLabel>
                            <AuthorName>{post.author.name}</AuthorName>
                            <AuthorBio>
                                As the founder of Luckynote, {post.author.name} is happy helping people organize their thoughts and improve their well-being. He believes in asking meaningful questions and finding elegant solutions to life's complexities.
                                <AuthorSocial>
                                    <SocialLink 
                                        href="https://x.com/lucky_uros" 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        aria-label="Follow on X"
                                    >
                                        <FaXTwitter size={18} />
                                    </SocialLink>
                                </AuthorSocial>
                            </AuthorBio>
                        </AuthorInfoContainer>
                    </AuthorCard>

                    {post.tags && (
                        <TagsSection>
                            {post.tags.map(tag => (
                                <Tag key={tag}>#{tag}</Tag>
                            ))}
                        </TagsSection>
                    )}
                    </Article>
                </BlogContainer>
            </BlogSurface>
        </LayoutDefault>
    );
};

const SERIF = `Georgia, 'Iowan Old Style', 'Palatino Linotype', 'Book Antiqua', Palatino, serif`;
const INK = '#14110f';
const MUTED = 'rgba(20, 17, 15, 0.66)';
const PAPER = '#fcf9f7';
const PAPER_DEEP = '#f6efe7';

const BlogSurface = styled.div`
    background:
        radial-gradient(circle at 14% 4%, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0) 26rem),
        linear-gradient(180deg, ${PAPER} 0%, ${PAPER_DEEP} 56%, ${PAPER} 100%);
    color: ${INK};
    min-height: 100dvh;
`;

const BlogContainer = styled.div`
    margin: 0 auto;
    max-width: 1064px;
    padding: 72px 24px 96px;

    @media (max-width: 768px) {
        padding: 44px 18px 72px;
    }
`;

const Article = styled.article`
    margin: 0 auto;
    max-width: 896px;
`;

const Breadcrumbs = styled.nav`
    align-items: center;
    color: rgba(20, 17, 15, 0.58);
    display: flex;
    flex-wrap: wrap;
    font-size: 14px !important;
    font-weight: 600;
    gap: 10px;
    line-height: 1.4;
    margin: 0 auto 34px;
    max-width: 896px;
`;

const BackToBlogs = styled(Link)`
    color: rgba(20, 17, 15, 0.72) !important;
    font-size: inherit !important;
    line-height: inherit !important;
    text-decoration: none;

    &:hover {
        color: ${INK} !important;
    }

    &:focus-visible {
        outline: 2px solid rgba(96, 67, 39, 0.35);
        outline-offset: 4px;
    }
`;

const BreadcrumbSeparator = styled.span`
    color: rgba(20, 17, 15, 0.32);
    font-size: 1.2rem;
`;

const BreadcrumbCategory = styled(Link)`
    color: rgba(20, 17, 15, 0.68) !important;
    font-size: inherit !important;
    font-weight: 650;
    line-height: inherit !important;
    text-decoration: none !important;

    &:hover,
    &:focus-visible {
        color: ${INK} !important;
    }

    &:focus-visible {
        outline: 2px solid rgba(96, 67, 39, 0.35);
        outline-offset: 4px;
    }
`;

const BlogHeader = styled.header`
    margin: 0 0 26px;
    max-width: 780px;
    text-align: left;

    h1 {
        color: ${INK};
        font-family: ${SERIF};
        font-size: 42px;
        font-weight: 700;
        letter-spacing: 0;
        line-height: 1.14;
        margin: 0 0 18px;
        text-wrap: balance;
    }

    @media (max-width: 640px) {
        h1 {
            font-size: 32px;
        }
    }
`;

const Dek = styled.p`
    color: rgba(43, 47, 54, 0.74);
    font-size: 18px;
    line-height: 1.62;
    margin: 0 0 22px;
    max-width: 720px;
    text-align: left;
    text-wrap: pretty;
`;

const BlogMeta = styled.div`
    color: rgba(20, 17, 15, 0.72);
    display: grid;
    gap: 8px;
    justify-content: start;
    margin: 0;
    max-width: 896px;
    text-align: left;
`;

const MetaRow = styled.div`
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const AuthorPill = styled.span`
    align-items: center;
    background: rgba(255, 255, 255, 0.74);
    border: 1px solid rgba(20, 17, 15, 0.1);
    border-radius: 10px;
    display: inline-flex;
    font-weight: 600;
    gap: 8px;
    padding: 5px 10px 5px 6px;
`;

const AuthorAvatar = styled.img`
    border-radius: 50%;
    height: 20px;
    object-fit: cover;
    width: 20px;
`;

const MetaPill = styled.span`
    background: rgba(255, 255, 255, 0.74);
    border: 1px solid rgba(20, 17, 15, 0.1);
    border-radius: 10px;
    color: ${INK};
    font-weight: 600;
    padding: 5px 10px;
`;

const CategoryTag = styled(Link)`
    background: rgba(255, 255, 255, 0.74);
    border: 1px solid rgba(20, 17, 15, 0.1);
    border-radius: 10px;
    color: rgba(20, 17, 15, 0.78) !important;
    font-weight: 600;
    padding: 5px 10px;
    text-decoration: none !important;

    &:hover,
    &:focus-visible {
        color: ${INK} !important;
        border-color: rgba(20, 17, 15, 0.2);
    }

    &:focus-visible {
        outline: 2px solid rgba(96, 67, 39, 0.35);
        outline-offset: 3px;
    }
`;

const FeaturedImage = styled.img`
    aspect-ratio: 2 / 1;
    background: rgba(20, 17, 15, 0.05);
    border: 1px solid rgba(20, 17, 15, 0.1);
    border-radius: 10px;
    display: block;
    margin: 32px 0;
    object-fit: cover;
    width: 100%;

    @media (max-width: 640px) {
        aspect-ratio: 5 / 3;
        margin: 26px 0;
    }
`;

const BlogContent = styled.div`
    color: rgba(43, 47, 54, 0.82);
    font-size: 18px;
    line-height: 1.72;

    h1, h2, h3, h4 {
        color: ${INK};
        font-family: ${SERIF};
        font-weight: 500;
        letter-spacing: 0;
        text-wrap: balance;
    }

    h1,
    h2 {
        font-size: clamp(30px, 3vw, 36px);
        line-height: 1.08;
        margin: 2.6em 0 0.7em;
    }

    h3 {
        font-size: 24px;
        line-height: 1.22;
        margin: 2em 0 0.6em;
    }

    h4 {
        font-size: 19px;
        line-height: 1.35;
        margin: 1.8em 0 0.6em;
    }

    strong {
        color: ${INK};
        font-weight: 700;
    }

    p {
        color: rgba(43, 47, 54, 0.82);
        font-size: 18px;
        line-height: 1.72;
        margin: 0 0 1.25em;
        text-wrap: pretty;
    }

    li,
    .blog-list-item {
        color: rgba(43, 47, 54, 0.82) !important;
        display: list-item !important;
        font-size: 18px;
        line-height: 1.65;
        margin-bottom: 0.55em !important;
        padding-left: 0.12em;
    }

    li > p {
        margin: 0 0 0.5em;
    }

    li > p > strong {
        color: ${INK};
        display: inline;
        margin: 0;
    }

    li > ul, li > ol {
        margin: 0.55em 0 1em 1.2em !important;
        padding-left: 1.2em !important;
    }

    ul, ol,
    .blog-list {
        color: rgba(43, 47, 54, 0.82) !important;
        list-style-position: outside !important;
        margin: 1em 0 1.45em !important;
        padding-left: 1.45em !important;
    }

    ul.blog-list {
        list-style-type: disc !important;
    }

    ol.blog-list {
        list-style-type: decimal !important;
    }

    ul.blog-list ul {
        list-style-type: circle !important;
    }

    ul.blog-list ul ul {
        list-style-type: square !important;
    }

    li::marker,
    .blog-list-item::marker {
        color: rgba(96, 67, 39, 0.72);
        font-weight: 700;
    }

    ol li::marker {
        font-variant-numeric: tabular-nums;
    }

    input[type='checkbox'] {
        accent-color: #604327;
        height: 16px;
        margin: 0 9px 0 -1.35em;
        transform: translateY(2px);
        width: 16px;
    }

    a {
        color: ${LINK} !important;
        font-weight: 600;
        text-decoration: underline !important;
        text-decoration-thickness: 1px !important;
        text-underline-offset: 0.16em;

        &:hover {
            color: #604327 !important;
        }
    }

    blockquote {
        background: rgba(255, 255, 255, 0.48);
        border-left: 3px solid rgba(96, 67, 39, 0.45);
        border-radius: 8px;
        color: rgba(20, 17, 15, 0.74);
        font-family: ${SERIF};
        font-size: 1.22rem;
        font-style: normal;
        line-height: 1.55;
        margin: 2em 0;
        padding: 18px 22px;
    }

    code {
        background: rgba(20, 17, 15, 0.08);
        border: 1px solid rgba(20, 17, 15, 0.08);
        border-radius: 5px;
        color: ${INK};
        font-family: 'SF Mono', Menlo, monospace;
        font-size: 0.86em;
        padding: 2px 5px;
    }

    pre {
        background: #1d1a17;
        border-radius: 10px;
        color: #fff;
        font-size: 0.88em;
        margin: 1.8em 0;
        overflow-x: auto;
        padding: 18px 20px;

        code {
            background: none;
            border: 0;
            color: inherit;
            padding: 0;
        }
    }

    .blog-rule {
        border: 0;
        border-top: 1px solid rgba(20, 17, 15, 0.12);
        margin: 2.2em 0;
    }

    .table-container {
        margin: 1.7em 0 2.1em;
        overflow-x: auto;
        padding: 0 0 8px;
        scrollbar-color: rgba(96, 67, 39, 0.32) transparent;
    }

    table {
        background: transparent;
        border-collapse: collapse;
        color: rgba(43, 47, 54, 0.82);
        font-size: 16px;
        line-height: 1.5;
        margin: 0;
        min-width: 680px;
        width: 100%;
    }

    thead {
        border-bottom: 1px solid rgba(20, 17, 15, 0.18);
    }

    th {
        background: transparent;
        color: ${INK};
        font-weight: 700;
        letter-spacing: 0;
        padding: 0 16px 13px 0;
        text-align: left;
        vertical-align: bottom;
    }

    td {
        border-bottom: 1px solid rgba(20, 17, 15, 0.08);
        color: rgba(43, 47, 54, 0.82);
        padding: 13px 16px 13px 0;
        vertical-align: top;
    }

    th:first-child,
    td:first-child {
        color: ${INK};
        font-weight: 650;
    }

    th:last-child,
    td:last-child {
        padding-right: 0;
    }

    tbody tr:last-child td {
        border-bottom: 0;
    }

    tbody tr {
        transition: background 160ms ease;
    }

    // tr:hover {
    //     background: rgba(20, 17, 15, 0.04);
    // }

    tbody tr:hover {
        background: rgba(255, 255, 255, 0.42);
    }

    blockquote:has(strong:first-child) {
        background: rgba(255, 255, 255, 0.58);
        border-left-color: rgba(20, 17, 15, 0.38);
        font-family: inherit;
        font-size: 1.05rem;
    }
`;

const MarkdownImage = styled.img`
    border-radius: 10px;
    display: block;
    margin: 2em auto;
    max-width: min(100%, 500px);
    width: auto;
`;

const MarkdownVideo = styled.video`
    border-radius: 10px;
    display: block;
    margin: 2em auto;
    max-width: 100%;
    width: 100%;
`;

const BlogDiscovery = styled.nav`
    margin: 84px auto 0;
    max-width: 940px;
    padding: 0 0 12px;
    text-align: center;
`;

const DiscoveryTitle = styled.h2`
    align-items: center;
    color: ${INK};
    display: inline-flex;
    font-family: ${SERIF};
    font-size: 48px;
    font-weight: 600;
    gap: 14px;
    letter-spacing: 0;
    line-height: 1.08;
    margin: 0 0 34px;
    text-wrap: balance;

    svg {
        color: rgba(20, 17, 15, 0.62);
        flex: 0 0 auto;
        height: 24px;
        margin-top: 8px;
        width: 24px;
    }

    @media (max-width: 640px) {
        align-items: flex-start;
        font-size: 34px;
        gap: 10px;
        margin-bottom: 24px;

        svg {
            height: 19px;
            margin-top: 7px;
            width: 19px;
        }
    }
`;

const DiscoveryLinks = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
    justify-content: center;

    @media (max-width: 640px) {
        gap: 10px;
        justify-content: flex-start;
        text-align: left;
    }
`;

const DiscoveryLink = styled(Link).attrs(props => ({
    style: {
        color: INK
    }
}))`
    align-items: center;
    background: ${props => props.$active ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.68)'};
    border: 1px solid ${props => props.$active ? 'rgba(20, 17, 15, 0.28)' : 'rgba(20, 17, 15, 0.16)'};
    border-radius: 9px;
    box-shadow: 0 12px 26px rgba(59, 46, 35, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.76);
    display: inline-flex;
    font-size: 18px !important;
    font-weight: 700;
    justify-content: center;
    line-height: 1.2 !important;
    min-height: 56px;
    min-width: 144px;
    padding: 16px 24px;
    text-decoration: none !important;
    transition: background 180ms ease, border-color 180ms ease, transform 180ms ease;

    &:hover {
        background: rgba(255, 255, 255, 0.94);
        border-color: rgba(20, 17, 15, 0.32);
        transform: translateY(-2px);
    }

    &:focus-visible {
        outline: 2px solid rgba(96, 67, 39, 0.35);
        outline-offset: 4px;
    }

    @media (max-width: 640px) {
        flex: 1 1 calc(50% - 10px);
        font-size: 15px !important;
        min-height: 48px;
        min-width: 0;
        padding: 12px 14px;
    }
`;

const SocialShare = styled.div`
    align-items: center;
    display: flex;
    gap: 18px;
    justify-content: center;
    margin: 28px 0 40px;
    padding: 22px 0;

    @media (max-width: 560px) {
        align-items: stretch;
        flex-direction: column;
    }
`;

const ShareText = styled.div`
    color: ${MUTED};
    font-size: 1rem;
    font-weight: 600;
`;

const ShareButtons = styled.div`
    display: flex;
    gap: 12px;
`;

const ShareButton = styled.a`
    align-items: center;
    background: rgba(255, 255, 255, 0.62);
    border: 1px solid rgba(20, 17, 15, 0.12);
    border-radius: 10px;
    color: ${INK} !important;
    display: flex;
    height: 44px;
    justify-content: center;
    transition: background 180ms ease, transform 180ms ease, border-color 180ms ease;
    width: 44px;

    svg {
        color: ${INK} !important;
        fill: ${INK} !important;
    }

    &:hover {
        background: rgba(255, 255, 255, 0.88);
        border-color: rgba(20, 17, 15, 0.22);
        transform: translateY(-2px);
    }

    &:focus-visible {
        outline: 2px solid rgba(96, 67, 39, 0.35);
        outline-offset: 4px;
    }
`;

const AuthorCard = styled.div`
    align-items: center;
    background: rgba(255, 255, 255, 0.52);
    border: 1px solid rgba(20, 17, 15, 0.1);
    border-radius: 12px;
    display: flex;
    gap: 22px;
    margin: 40px 0;
    padding: 22px;

    @media (max-width: 640px) {
        align-items: flex-start;
        flex-direction: column;
    }
`;

const AuthorImage = styled.img`
    border: 1px solid rgba(20, 17, 15, 0.12);
    border-radius: 20px;
    height: 96px;
    object-fit: cover;
    width: 96px;
`;

const AuthorInfoContainer = styled.div`
    flex: 1;
`;

const AuthorLabel = styled.div`
    color: rgba(20, 17, 15, 0.48);
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    margin-bottom: 5px;
    text-transform: uppercase;
`;

const AuthorName = styled.div`
    color: ${INK};
    font-family: ${SERIF};
    font-size: 1.45rem;
    font-weight: 700;
    line-height: 1.15;
    margin-bottom: 8px;
`;

const AuthorBio = styled.div`
    color: ${MUTED};
    font-size: 0.98rem;
    line-height: 1.62;
`;

const AuthorSocial = styled.div`
    margin-top: 12px;
`;

const SocialLink = styled.a`
    align-items: center;
    background: rgba(255, 255, 255, 0.64);
    border: 1px solid rgba(20, 17, 15, 0.12);
    border-radius: 10px;
    color: ${INK} !important;
    display: inline-flex;
    height: 36px;
    justify-content: center;
    text-decoration: none;
    transition: background 180ms ease, transform 180ms ease;
    width: 36px;

    svg {
        color: ${INK} !important;
        fill: ${INK} !important;
    }

    &:hover {
        background: rgba(255, 255, 255, 0.9);
        transform: translateY(-2px);
    }
`;

const TagsSection = styled.div`
    border-top: 1px solid rgba(20, 17, 15, 0.1);
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 40px;
    padding-top: 22px;
`;

const Tag = styled.span`
    background: rgba(255, 255, 255, 0.52);
    border: 1px solid rgba(20, 17, 15, 0.12);
    border-radius: 999px;
    color: rgba(20, 17, 15, 0.72);
    font-size: 0.9rem;
    font-weight: 600;
    padding: 6px 12px;
`;

const NotFound = styled.div`
    color: ${MUTED};
    padding: 90px 0;
    text-align: center;

    h1 {
        color: ${INK};
        font-family: ${SERIF};
        margin-bottom: 16px;
    }

    p {
        color: ${MUTED};
        margin-bottom: 24px;
    }
`;

export default BlogPost;
